import { useEffect, useMemo, useState } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/lib/api';
import { Link } from 'react-router-dom';
import { Search, Mail, Volume2, Music, Youtube } from 'lucide-react';
import { estimateDifficulty, DIFFICULTY_LABEL_FR, difficultyDelta } from '@/lib/difficultyEstimator';
import useSongEmbeddability from '@/lib/useSongEmbeddability';

const instruments = ['guitar', 'piano', 'voice', 'drums', 'bass'];
const genres = ['rock', 'pop', 'jazz', 'blues', 'folk', 'classical', 'electronic'];
const difficulties = ['beginner', 'intermediate', 'advanced', 'pro'];

const iLabel = { guitar: 'Guitare', piano: 'Piano', voice: 'Chant', drums: 'Batterie', bass: 'Basse' };

export default function SongLibrary() {
  const [songs, setSongs] = useState([]);
  const [q, setQ] = useState('');
  const [instrument, setInstrument] = useState('');
  const [genre, setGenre] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [syncFilter, setSyncFilter] = useState(''); // '', 'sync', 'metronome'
  const embedMap = useSongEmbeddability(songs);

  useEffect(() => {
    (async () => {
      const params = {};
      if (instrument) params.instrument = instrument;
      if (genre) params.genre = genre;
      if (difficulty) params.difficulty = difficulty;
      if (q) params.search = q;
      const { data } = await api.get('/songs', { params });
      setSongs(data);
    })();
  }, [instrument, genre, difficulty, q]);

  return (
    <Layout>
      <div className="p-6 md:p-10">
        <div className="mb-8 rise flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="overline text-orange-400 mb-2">Bibliothèque</div>
            <h1 className="font-display text-5xl tracking-tight">Toutes les chansons</h1>
          </div>
          <Link to="/requests" data-testid="library-request-btn" className="btn-ghost inline-flex items-center gap-2 text-sm">
            <Mail size={15} /> Suggérer une chanson
          </Link>
        </div>

        <div className="mb-6 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-500" />
            <input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher un titre ou un artiste..."
              className="w-full pl-10 pr-4 py-3 rounded-full bg-stone-900 border border-stone-800 text-stone-100 placeholder-stone-500 focus:outline-none focus:border-orange-800"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          <Chip label="Tous instruments" active={!instrument} onClick={() => setInstrument('')} tid="chip-inst-all" />
          {instruments.map(i => (
            <Chip key={i} label={iLabel[i]} active={instrument === i} onClick={() => setInstrument(i)} tid={`chip-inst-${i}`} />
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mb-8">
          <Chip label="Tous genres" active={!genre} onClick={() => setGenre('')} tid="chip-genre-all" />
          {genres.map(g => (
            <Chip key={g} label={g} active={genre === g} onClick={() => setGenre(g)} tid={`chip-genre-${g}`} />
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mb-6">
          <Chip label="Tous niveaux" active={!difficulty} onClick={() => setDifficulty('')} tid="chip-diff-all" />
          {difficulties.map(d => (
            <Chip key={d} label={d} active={difficulty === d} onClick={() => setDifficulty(d)} tid={`chip-diff-${d}`} />
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mb-10" data-testid="sync-filter-row">
          <Chip label="Toutes chansons" active={!syncFilter} onClick={() => setSyncFilter('')} tid="chip-sync-all" />
          <Chip label="🎧 Audio synchro dispo" active={syncFilter === 'sync'} onClick={() => setSyncFilter('sync')} tid="chip-sync-yes" />
          <Chip label="🎹 Mode métronome" active={syncFilter === 'metronome'} onClick={() => setSyncFilter('metronome')} tid="chip-sync-no" />
        </div>

        {songs.length === 0 ? (
          <div className="text-stone-400 py-20 text-center font-display text-2xl">Aucune chanson trouvée.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {songs.map((s, idx) => {
              const est = estimateDifficulty(s.chords, s.bpm);
              const delta = est.level ? difficultyDelta(est.level, s.difficulty) : 0;
              const embedStatus = embedMap[s.id] || 'unknown';
              if (syncFilter === 'sync' && !(embedStatus === 'sync' || embedStatus === 'audio')) return null;
              if (syncFilter === 'metronome' && embedStatus !== 'metronome') return null;
              const badge =
                embedStatus === 'audio'
                  ? { txt: 'Audio synchro', icon: <Volume2 size={9} />, cls: 'bg-emerald-950/60 text-emerald-300 border-emerald-800/60' }
                  : embedStatus === 'sync'
                    ? { txt: 'YouTube synchro', icon: <Youtube size={9} />, cls: 'bg-red-950/60 text-red-300 border-red-800/60' }
                    : embedStatus === 'metronome'
                      ? { txt: 'Métronome seul', icon: <Music size={9} />, cls: 'bg-amber-950/50 text-amber-300 border-amber-800/60' }
                      : null;
              return (
              <Link
                key={s.id}
                to={`/song/${s.id}`}
                data-testid={`library-song-${idx}`}
                className="card-stone overflow-hidden hover:border-orange-800/60 transition-colors group rise"
                style={{ animationDelay: `${Math.min(idx, 12) * 40}ms` }}
              >
                <div className="aspect-[4/3] bg-stone-800 overflow-hidden">
                  {s.cover_url && (
                    <img src={s.cover_url} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  )}
                </div>
                <div className="p-4">
                  <div className="overline text-stone-500 flex items-center gap-2 flex-wrap">
                    <span>{s.genre} · {s.difficulty}</span>
                    {s.is_original && <span className="text-[9px] px-1.5 py-0.5 rounded bg-orange-950/60 text-orange-300 border border-orange-800/50">ORIGINAL</span>}
                    {badge && (
                      <span
                        data-testid={`song-sync-${idx}`}
                        className={`inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded border font-medium ${badge.cls}`}
                      >
                        {badge.icon} {badge.txt}
                      </span>
                    )}
                    {est.level && delta !== 0 && (
                      <span
                        title={`Estimation auto (${est.reasons.join(', ')})`}
                        data-testid={`song-est-${idx}`}
                        className={`text-[9px] px-1.5 py-0.5 rounded border font-medium ${
                          delta > 0
                            ? 'bg-amber-950/50 text-amber-300 border-amber-800/60'
                            : 'bg-emerald-950/40 text-emerald-300 border-emerald-800/60'
                        }`}
                      >
                        {delta > 0 ? '↑ ' : '↓ '}Estimé {DIFFICULTY_LABEL_FR[est.level].toLowerCase()}
                      </span>
                    )}
                  </div>
                  <div className="font-display text-2xl mt-1 leading-tight">{s.title}</div>
                  <div className="text-stone-400 text-sm">{s.artist}</div>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {s.instruments?.map(i => (
                      <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-stone-800 text-stone-300">{iLabel[i] || i}</span>
                    ))}
                  </div>
                </div>
              </Link>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}

function Chip({ label, active, onClick, tid }) {
  return (
    <button
      data-testid={tid}
      onClick={onClick}
      className={`px-4 py-2 rounded-full text-sm border transition-colors ${
        active
          ? 'bg-orange-700 border-orange-700 text-white'
          : 'bg-transparent border-stone-700 text-stone-300 hover:border-stone-500'
      }`}
    >
      {label}
    </button>
  );
}
